import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { init, dispose, ActionType, CandleType } from 'klinecharts'
import type { Chart, KLineData, VisibleRange } from 'klinecharts'
import { buildStyles, defaultBackground, EMPTY_TOOLTIP } from '@/components/Workbench/chartStyles'
import { registerVolumeOverlay, VOLUME_INDICATOR, volColors } from '@/components/Workbench/volumeOverlay'
import { useIsDark } from '@/components/Workbench/useIsDark'
import { getCandleCountForTimeframe, getTimeframeSeconds } from '../../utils/chartUtils'
import { fetchSymbolMeta, precisionFromPointSize } from '../adapters/symbolsFetcher'
import type { CandleData, OrderData } from '../types/chartData'
import { backtestOrderConnector } from '@/components/Workbench/customOverlays'
import {
  ORDER_LINES,
  registerBacktestChartOverlays,
  TRADE_CONNECTOR,
  TRADE_MARKER,
  type OrderLinesExtendData,
} from './backtestChartOverlays'
import { closesByEntryTime, tradePips } from '../utils/orderPairing'
import {
  getIndexRange,
  getVisibleBarCount as engineVisibleBarCount,
  setIndexRange,
  setPanBounds,
  rightPanBoundPx,
  MIN_RIGHT_PAN_BOUND,
  type IndexRange,
} from './klineViewport'
import type { FormingBarState } from '../utils/formingBarBuffer'
import BacktestChartLegend from './BacktestChartLegend'
import BacktestOrderTooltip from './BacktestOrderTooltip'

/**
 * The Backtest tab's price chart, on klinecharts + the datafeed WebSocket (the
 * same engine and transport as the Workbench).
 *
 * The chart is a dumb mirror of the `candles` prop: replay reveals bars by
 * shortening that array upstream (useBacktestChartState slices at the reveal
 * boundary), and the viewport is driven from outside through the imperative
 * handle. This component owns no data loading and no replay state.
 *
 * `candles` / `orders` carry unix SECONDS; klinecharts wants milliseconds, and
 * this file is the only place that conversion happens.
 */

const DEFAULT_PRECISION = 5
/**
 * Ceiling for markers AND connectors together: klinecharts walks every overlay
 * on each repaint, and this chart repaints on every replay tick. Markers are
 * spent first (they're the primary signal), connectors take the remainder.
 * Newest win. Windowed orders mean neither normally binds.
 */
const MAX_OVERLAYS = 800

const MARKER_NEUTRAL = '#9E9E9E'
const MARKER_WIN = '#00C853'
const MARKER_LOSS = '#FF1744'

export interface BacktestChartHandle {
  setVisibleLogicalRange: (range: IndexRange) => void
  getVisibleLogicalRange: () => IndexRange | null
  getCandleCount: () => number
  /**
   * How many bars fit on screen at the current zoom.
   *
   * Deliberately NOT `getVisibleLogicalRange().to - .from`, which is a bar short whenever the
   * range is clamped — at the live edge, and under a forming bar. A caller that re-applies a
   * span on every replay tick needs the exact one. See klineViewport.getVisibleBarCount.
   */
  getVisibleBarCount: () => number | null
  /**
   * Paint (or clear) the bar currently forming just right of the last closed one.
   *
   * Driven imperatively at ~30fps by useFormingBar during tick replay, so React
   * never re-renders per frame. The forming bar lives only in the engine — it is
   * NOT part of `candles`, and `getCandleCount()` keeps reporting closed bars
   * only. Passing null removes it.
   *
   * `revealTs` is the sub-bar cursor, in unix seconds. Given it, trade markers
   * and connectors are reconciled at that instant, so a fill part-way through the
   * bar shows its arrow before the bar closes. Omit it to leave overlays at bar
   * granularity.
   */
  setFormingBar: (candle: FormingBarState | null, revealTs?: number) => void
}

interface BacktestChartProps {
  candles: CandleData[]
  orders?: OrderData[]
  symbol: string
  timeframe: string
  /** Reveal ceiling while a backtest is still running (unix seconds). */
  maxAvailableTimestamp?: number
  /** Fit the newest bars on first paint. Off when a parent owns the viewport. */
  autoSetInitialVisibleRange?: boolean
  onVisibleRangeChange?: (range: IndexRange) => void
  onUserNavigate?: () => void
  /**
   * Hands the raw klinecharts instance up (null on dispose), mirroring
   * WorkbenchChart.onReady.
   *
   * The imperative handle above covers everything replay needs, but a caller that wants to
   * paint its OWN overlays on this chart needs the engine itself. The walkthrough does: it
   * repaints the user's saved drawings alongside the order markers.
   */
  onChartReady?: (chart: Chart | null) => void
  /** Draw the volume overlay. Off for callers that want a bare price chart. */
  showVolume?: boolean
  /**
   * Stop panning at the loaded data instead of letting the user drag off into blank
   * space (klinecharts allows ~visibleBarCount - 2 bar widths past the newest bar).
   *
   * Opt-in, not the default: the Backtest tab has always panned this way, and the
   * walkthrough is the case where it actively misleads — the user is validating one
   * specific drawn range, so scrolling past its end shows nothing but the last bar's
   * price line stretched over emptiness.
   */
  boundPanToData?: boolean
  /**
   * Bar widths of blank space held right of the newest bar, whenever the view is pinned to
   * the live edge.
   *
   * For a replay: with no margin the bar being revealed appears hard against the price axis,
   * which is the one place on the chart the eye is least able to read it. A few bars of run-off
   * give each new candle somewhere to land. Costs the same number of bars off the left of the
   * window — the span is not re-zoomed to make room.
   *
   * Read once at init for the pan bound it implies, like `boundPanToData` — pass a constant.
   */
  rightPadBars?: number
  /**
   * Hold the zoom at this bar width, in px, and let only the user change it. Off by default.
   *
   * Without it the chart zooms whenever it is framed: every range the replay asks for — each play
   * tick on the Backtest tab, each step, scrub, trade row and window reload everywhere — is sized
   * to the timeframe's default screen, and setIndexRange zooms to fit it. With it, a framing is a
   * SCROLL (see setIndexRange's `keepZoom`): the range says where to look, never how close. The
   * engine's own bar space is the one source of truth after init, and only a zoom gesture — wheel,
   * pinch, a drag on the time axis — moves it; `onBarSpaceChange` reports each one.
   *
   * Read once at init, like `rightPadBars` — pass a constant.
   */
  fixedBarSpace?: number
  /** The user zoomed: the bar width they chose, in px. Only fires for gestures, never for framing. */
  onBarSpaceChange?: (barSpace: number) => void
  /**
   * The walkthrough's chart rules. Opt-in, so the Backtest tab is unaffected.
   *
   * Two rules, both about not telling the trader more than the moment knows:
   *  - a position's entry / stop-loss / take-profit are drawn as working lines while it is
   *    OPEN, and disappear when it closes (the arrows and their connector take over);
   *  - an entry arrow stays neutral grey until its position closes, then flips to
   *    win / loss — so watching a replay does not reveal the outcome at the entry.
   *
   * Also lights one trade's lines and dims the others on hover, while paused.
   */
  liveOrderLines?: boolean
  /** From backtest_config.pip_size. Puts a pip distance on each level's label. */
  pipSize?: number | null
  /**
   * entry time -> ticket number, for naming orders in the click tooltip.
   *
   * Passed in rather than counted here: this chart only holds the orders for the candle window
   * it has paged in, so anything it counted for itself would renumber on a pan and disagree
   * with the trades pane. The walkthrough supplies it (useWalkthroughOrders); the Backtest tab
   * has no ticket numbers and omits it, leaving the tooltip exactly as it was.
   */
  orderTickets?: ReadonlyMap<number, number>
}

function toKline(c: CandleData): KLineData {
  return {
    timestamp: c.time * 1000,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    volume: c.volume,
  }
}

/**
 * klinecharts prints a dev-only "Welcome to klinecharts" banner synchronously
 * inside init() with no option to disable it — mute console.log for just that
 * one line while init() runs.
 */
function initQuietly(el: HTMLElement): Chart | null {
  const nativeLog = console.log
  console.log = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('Welcome to klinecharts')) return
    nativeLog(...args)
  }
  try {
    return init(el)
  } finally {
    console.log = nativeLog
  }
}

/**
 * How far past either end of the data `boundPanToData` still lets the user pan.
 *
 * A sliver — ~3 bars at the default bar space, versus the ~48 the engine allows by
 * default — and not a px less than MIN_RIGHT_PAN_BOUND, or the same cap that stops the
 * user would also clip the forming bar's half-bar margin at high zoom.
 */
const PAN_MARGIN_PX = MIN_RIGHT_PAN_BOUND

/**
 * The visible range in CLOSED-bar index space.
 *
 * While a forming bar is painted, the engine holds one element more than
 * `displayCandles` and so reports an inclusive `to` of `closedCount`. The parent
 * (useGraphCandleWindow) clamps ranges against `effectiveCandleCount - 1`, so
 * handing it the engine's index would make it "correct" a range that isn't wrong
 * — pushing the view a bar right and hiding the forming bar on any mid-bar
 * re-render. Clamping here keeps the whole external contract in closed-bar space.
 */
function closedIndexRange(c: Chart, closedCount: number): IndexRange | null {
  const r = getIndexRange(c)
  if (!r) return null
  const to = Math.min(r.to, closedCount - 1)
  return { from: Math.min(r.from, to), to }
}

/** Index of the last bar at or before `ts`, or -1 when `ts` precedes the window. */
function findBarIndex(candles: CandleData[], ts: number): number {
  let lo = 0
  let hi = candles.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (candles[mid].time <= ts) lo = mid + 1
    else hi = mid
  }
  return lo - 1
}

interface MarkerSpec {
  key: string
  barTime: number
  value: number
  dir: 'up' | 'down'
  color: string
  text: string
}

/**
 * A single order's realised PnL, or null while the outcome is not known yet.
 *
 * `closes` is the walkthrough's entry-time pairing. With it, an `opened` event can be asked
 * how its position ENDED, which is what lets an entry arrow stay grey while the trade runs
 * and flip to win/loss the moment it closes — the trader watching a replay should not be
 * told the answer at the point the code committed to the trade.
 *
 * Without it (the Backtest tab), an `opened` event has no outcome and never gains one, which
 * is exactly the behaviour that shipped.
 */
function orderOutcome(o: OrderData, closes: Map<number, OrderData> | null): number | null {
  if (o.eventType === 'closed') return o.pnl ?? 0
  if (!closes) return null
  // On an `opened` event, `time` IS the position's entry time.
  const close = closes.get(o.time)
  return close ? (close.pnl ?? 0) : null
}

/**
 * Group orders onto bars and resolve each group's arrow. Rules carried over from
 * the lightweight-charts implementation: one marker per (bar, side); grey while
 * nothing is closed or when a group mixes outcomes, otherwise PnL-coloured; a
 * count badge once a group holds more than one order.
 */
function buildMarkers(
  candles: CandleData[],
  orders: OrderData[],
  timeframe: string,
  forming: FormingBarState | null = null,
  closes: Map<number, OrderData> | null = null,
): MarkerSpec[] {
  if ((candles.length === 0 && forming === null) || orders.length === 0) return []

  const tfSec = getTimeframeSeconds(timeframe)
  const lastBarTime = forming !== null ? forming.time : candles[candles.length - 1].time
  const lastBarEnd = lastBarTime + tfSec
  const groups = new Map<string, { bar: CandleData; side: 'buy' | 'sell'; orders: OrderData[] }>()

  for (const order of orders) {
    if (order.time >= lastBarEnd) continue
    // The forming bar is not part of `candles`, so an order at or after its start
    // belongs to it. Taking the bar object rather than an index is what lets that
    // work without splicing it into the array on every animation frame.
    const bar =
      forming !== null && order.time >= forming.time
        ? forming
        : candles[findBarIndex(candles, order.time)]
    // findBarIndex returns -1 for an order earlier than the loaded window.
    if (!bar) continue
    const side = order.type === 'BUY' ? 'buy' : 'sell'
    const key = `${bar.time}:${side}`
    const group = groups.get(key)
    if (group) group.orders.push(order)
    else groups.set(key, { bar, side, orders: [order] })
  }

  const specs: MarkerSpec[] = []
  for (const { bar, side, orders: grouped } of groups.values()) {
    const resolved = grouped
      .map((o) => orderOutcome(o, closes))
      .filter((pnl): pnl is number => pnl !== null)
    const color =
      resolved.length === 0
        ? MARKER_NEUTRAL
        : resolved.length === 1 && grouped.length === 1
          ? resolved[0] > 0
            ? MARKER_WIN
            : MARKER_LOSS
          : MARKER_NEUTRAL
    const dir = side === 'buy' ? 'up' : 'down'
    const text = grouped.length > 1 ? String(grouped.length) : ''
    // Anchor below the low / above the high, reproducing belowBar / aboveBar.
    const value = side === 'buy' ? bar.low : bar.high
    specs.push({
      // The key encodes everything that affects the drawing, so an open→closed
      // recolour replaces exactly one overlay and nothing else churns. The anchor
      // is included because a marker on the still-forming bar has to follow that
      // bar's low/high as they extend; a closed bar's never move, so this costs
      // nothing for the overwhelming majority.
      key: `${bar.time}:${dir}:${color}:${text}:${value}`,
      barTime: bar.time,
      value,
      dir,
      color,
      text,
    })
  }

  specs.sort((a, b) => a.barTime - b.barTime)
  return specs.length > MAX_OVERLAYS ? specs.slice(specs.length - MAX_OVERLAYS) : specs
}

interface ConnectorSpec {
  key: string
  /** Sort/prune anchor: the exit, i.e. when the connector becomes drawable. */
  exitTime: number
  points: Array<{ timestamp: number; value: number }>
  color: string
}

/**
 * Entry→exit connectors for closed orders, mapped through the Workbench's
 * `backtestOrderConnector` so both charts draw provably the same thing.
 *
 * `budget` is whatever the markers left of MAX_OVERLAYS.
 *
 * A connector is only built once BOTH endpoints' bars are loaded. klinecharts
 * resolves an out-of-range timestamp to the nearest bar, so an entry earlier
 * than the loaded window would bend the line flat onto the left edge. Skipped
 * orders materialize on a later pass, once extendWindow('left') reaches their
 * entry bar — the same deferral the Workbench does. This matters because
 * useGraphCandleWindow prunes orders by EXIT time, so an order can outlive the
 * bar its entry sits on.
 */
function buildConnectors(
  candles: CandleData[],
  orders: OrderData[],
  timeframe: string,
  budget: number,
  forming: FormingBarState | null = null,
): ConnectorSpec[] {
  if (budget <= 0 || candles.length === 0 || orders.length === 0) return []

  const firstBarTime = candles[0].time
  // Unlike a marker, a connector's endpoints are real fill prices, so nothing
  // about it moves as the forming bar grows — it only needs the extended end so
  // an exit inside that bar becomes drawable.
  const lastBarTime = forming !== null ? forming.time : candles[candles.length - 1].time
  const lastBarEnd = lastBarTime + getTimeframeSeconds(timeframe)

  const specs: ConnectorSpec[] = []
  for (const order of orders) {
    const conn = backtestOrderConnector(order)
    if (!conn) continue // not a closed order, or no entry data
    if (order.entryTime == null) continue
    if (order.entryTime < firstBarTime || order.time >= lastBarEnd) continue
    specs.push({
      // orderId is unique and stable; folding the colour in means a PnL
      // correction replaces exactly this one overlay.
      key: `${order.orderId}:${conn.color}`,
      exitTime: order.time,
      points: conn.points,
      color: conn.color,
    })
  }

  specs.sort((a, b) => a.exitTime - b.exitTime)
  return specs.length > budget ? specs.slice(specs.length - budget) : specs
}

interface OrderLinesSpec {
  key: string
  entryTime: number
  points: Array<{ timestamp: number; value: number }>
  dir: 'buy' | 'sell'
  entryLabel: string
  slLabel: string
  tpLabel: string
}

/**
 * The working lines of every position that is OPEN as of the reveal cursor.
 *
 * A position is open when its entry has been revealed and its close has not, so the set
 * shrinks by one the instant a trade closes and its lines are simply no longer wanted —
 * which is what makes the reconcile delete them without any teardown code of its own. That
 * "the stop-loss is on the chart exactly while the stop-loss is real" property is the whole
 * reason the lines exist: a chart littered with the stops of finished trades protects
 * nothing and reads as if the positions were still live.
 *
 * The x anchor barely matters — the overlay draws its rules from pane edge to pane edge —
 * but klinecharts snaps an out-of-range timestamp to the nearest bar, so anchoring on a
 * loaded bar keeps the engine from having to guess.
 */
function buildOrderLines(
  candles: CandleData[],
  orders: OrderData[],
  closes: Map<number, OrderData>,
  pipSize: number | null,
  budget: number,
): OrderLinesSpec[] {
  if (budget <= 0 || candles.length === 0 || orders.length === 0) return []

  const firstBarTime = candles[0].time
  const lastBarTime = candles[candles.length - 1].time

  const pipLabel = (role: string, entry: number, level: number, type: 'BUY' | 'SELL') => {
    if (!pipSize) return role
    const pips = tradePips(type, entry, level, pipSize)
    if (pips == null) return role
    return `${role} · ${Math.abs(Math.round(pips))} pips`
  }

  const specs: OrderLinesSpec[] = []
  for (const order of orders) {
    if (order.eventType !== 'opened') continue
    if (closes.has(order.time)) continue // already closed as far as the playhead knows
    const anchor = Math.min(Math.max(order.time, firstBarTime), lastBarTime)
    const sl = order.sl || null
    const tp = order.tp || null
    specs.push({
      // Everything that affects the drawing is in the key, so a broker-side stop move
      // replaces exactly this one overlay and no other trade's lines repaint.
      key: `${order.orderId}:${order.price}:${sl ?? '-'}:${tp ?? '-'}`,
      entryTime: order.time,
      // Three points, always in this order. A null level still occupies its slot as the
      // entry price so the point count is stable; the overlay's own figure list is what
      // decides not to draw it (see `hasSl` / `hasTp` in extendData's labels).
      points: [
        { timestamp: anchor * 1000, value: order.price },
        { timestamp: anchor * 1000, value: sl ?? order.price },
        { timestamp: anchor * 1000, value: tp ?? order.price },
      ],
      dir: order.type === 'BUY' ? 'buy' : 'sell',
      entryLabel: order.type === 'BUY' ? 'Buy' : 'Sell',
      slLabel: sl == null ? '' : pipLabel('S/L', order.price, sl, order.type),
      tpLabel: tp == null ? '' : pipLabel('T/P', order.price, tp, order.type),
    })
  }

  // Newest first out of the budget: the trade the user is watching is the one that just
  // opened. In practice a handful of positions are open at once and this never binds.
  specs.sort((a, b) => a.entryTime - b.entryTime)
  return specs.length > budget ? specs.slice(specs.length - budget) : specs
}

/** One place the overlay's extendData is shaped, because two callers write it. */
function orderLineExtendData(
  spec: OrderLinesSpec,
  precision: number,
  dimmed: boolean,
): OrderLinesExtendData {
  return {
    dir: spec.dir,
    dimmed,
    hasSl: spec.slLabel !== '',
    hasTp: spec.tpLabel !== '',
    entryLabel: spec.entryLabel,
    slLabel: spec.slLabel,
    tpLabel: spec.tpLabel,
    precision,
  }
}

const BacktestChart = forwardRef<BacktestChartHandle, BacktestChartProps>(function BacktestChart(
  {
    candles,
    orders = [],
    symbol,
    timeframe,
    maxAvailableTimestamp,
    autoSetInitialVisibleRange = true,
    onVisibleRangeChange,
    onUserNavigate,
    onChartReady,
    showVolume = true,
    boundPanToData = false,
    rightPadBars = 0,
    fixedBarSpace,
    onBarSpaceChange,
    liveOrderLines = false,
    pipSize = null,
    orderTickets,
  },
  ref,
) {
  const isDark = useIsDark()
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<Chart | null>(null)
  const [chart, setChart] = useState<Chart | null>(null)
  const [pricePrecision, setPricePrecision] = useState<number | null>(null)
  // Bumped on every data application so the legend re-reads the engine's list.
  const [dataNonce, setDataNonce] = useState(0)

  const onVisibleRangeChangeRef = useRef(onVisibleRangeChange)
  onVisibleRangeChangeRef.current = onVisibleRangeChange
  const onUserNavigateRef = useRef(onUserNavigate)
  onUserNavigateRef.current = onUserNavigate
  const timeframeRef = useRef(timeframe)
  timeframeRef.current = timeframe
  // Read inside the mount-once init effect, so it must not be a dependency.
  const showVolumeRef = useRef(showVolume)
  showVolumeRef.current = showVolume
  const boundPanToDataRef = useRef(boundPanToData)
  boundPanToDataRef.current = boundPanToData
  const rightPadBarsRef = useRef(rightPadBars)
  rightPadBarsRef.current = rightPadBars
  const fixedBarSpaceRef = useRef(fixedBarSpace)
  fixedBarSpaceRef.current = fixedBarSpace
  const onBarSpaceChangeRef = useRef(onBarSpaceChange)
  onBarSpaceChangeRef.current = onBarSpaceChange

  const dragStartXRef = useRef<number | null>(null)
  const dragNavigatedRef = useRef(false)
  /** Where a single-finger touch began, for the tap-vs-pan test in handleTouchEnd. */
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const initialRangeSetRef = useRef(false)
  // Last-applied series shape, for strict-suffix-append detection.
  const snapshotRef = useRef<{ firstTime: number; lastTime: number; count: number } | null>(null)
  // markerKey / connectorKey -> overlay id, so each data change only touches
  // the delta. In the replay steady state that delta is zero or one.
  const markerIdsRef = useRef<Map<string, string>>(new Map())
  const connectorIdsRef = useRef<Map<string, string>>(new Map())
  const orderLineIdsRef = useRef<Map<string, string>>(new Map())
  /** The spec behind each live order-line overlay, so a dim toggle can rebuild extendData. */
  const orderLineSpecsRef = useRef<Map<string, OrderLinesSpec>>(new Map())
  /**
   * The order-line set currently lit, or null when every set is at full strength.
   *
   * A ref rather than state: dimming is applied by overriding the overlays' extendData in
   * place, so nothing about it needs a React render, and a render here would fight the
   * 30fps forming-bar path.
   */
  const hoveredOrderKeyRef = useRef<string | null>(null)
  // Bar time (unix s) of the forming bar currently in the engine, or null.
  const formingBarRef = useRef<number | null>(null)
  // klinecharts fires OnVisibleRangeChange synchronously from inside updateData
  // — even for an in-place overwrite of the last bar. Left unsuppressed, every
  // animation frame would look like a user pan to useGraphCandleWindow and kick
  // off an extendWindow('right') fetch. The action is synchronous, so a plain
  // flag around the call is enough.
  const formingSuppressRef = useRef(false)
  // Notified after each forming-bar repaint. The engine gives no usable signal
  // of its own here — updateData's recalculateCrosshair reuses the existing
  // crosshair object, so OnCrosshairChange never fires — and bumping React state
  // would re-render the whole chart 30x a second. This lets the legend alone
  // follow the candle as it builds.
  const repaintListenersRef = useRef<Set<() => void>>(new Set())

  const [tooltip, setTooltip] = useState<{
    orders: OrderData[]
    point: { x: number; y: number }
  } | null>(null)

  const effectivePrecision = pricePrecision ?? DEFAULT_PRECISION
  const precisionRef = useRef(effectivePrecision)
  precisionRef.current = effectivePrecision

  // Reveal ceiling for a still-running backtest: drop bars the engine hasn't
  // produced yet. The parent applies the same clip; this is the last guard.
  const displayCandles = useMemo(() => {
    if (maxAvailableTimestamp == null || candles.length === 0) return candles
    const idx = findBarIndex(candles, maxAvailableTimestamp)
    return idx === candles.length - 1 ? candles : candles.slice(0, idx + 1)
  }, [candles, maxAvailableTimestamp])

  const displayCandlesRef = useRef(displayCandles)
  displayCandlesRef.current = displayCandles

  const ordersRef = useRef(orders)
  ordersRef.current = orders
  const liveOrderLinesRef = useRef(liveOrderLines)
  liveOrderLinesRef.current = liveOrderLines
  const pipSizeRef = useRef(pipSize)
  pipSizeRef.current = pipSize

  const hideTooltip = useCallback(() => setTooltip(null), [])

  const subscribeRepaint = useCallback((listener: () => void) => {
    const listeners = repaintListenersRef.current
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  // ---- Init / dispose -------------------------------------------------------
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    registerVolumeOverlay() // no-op after first call
    registerBacktestChartOverlays()

    const instance = initQuietly(el)
    chartRef.current = instance
    if (!instance) return undefined

    // Before any data: these set limits without re-adjusting, so a view that is
    // already overscrolled would stay that way.
    if (boundPanToDataRef.current) {
      // The right bound caps our own live-edge writes as well as the user's drags, so a
      // chart that reserves `rightPadBars` of margin has to leave room for it.
      setPanBounds(instance, PAN_MARGIN_PX, rightPanBoundPx(rightPadBarsRef.current))
    }
    // Before any data too, so the first frame is already at the chosen zoom.
    if (fixedBarSpaceRef.current != null) instance.setBarSpace(fixedBarSpaceRef.current)

    // TradingView-style volume: drawn in pixel space at the bottom of the price
    // pane, so it needs no extra pane and never squashes the candles.
    // EMPTY_TOOLTIP suppresses the engine's canvas legend, which would
    // otherwise paint "VOL_OVERLAY" on top of our HTML one.
    if (showVolumeRef.current) {
      instance.createIndicator(
        {
          name: VOLUME_INDICATOR,
          extendData: volColors(false),
          createTooltipDataSource: EMPTY_TOOLTIP,
        },
        true,
        { id: 'candle_pane' },
      )
    }

    // No setLoadDataCallback: history paging is owned by useGraphCandleWindow.
    // Every applyNewData passes `more: false`, which also stops the engine from
    // asking. Two paginators would fight over the same window.

    const onRange = (data?: unknown) => {
      // A forming-bar repaint is not navigation: swallow it before it closes the
      // tooltip or reaches the parent's pan/paging logic.
      if (formingSuppressRef.current) return
      hideTooltip()
      const vr = data as VisibleRange | undefined
      if (!vr || !onVisibleRangeChangeRef.current) return
      // The engine's `to` is exclusive; the parent works in inclusive indices.
      onVisibleRangeChangeRef.current({ from: vr.from, to: Math.max(vr.from, vr.to - 1) })
    }
    instance.subscribeAction(ActionType.OnVisibleRangeChange, onRange)

    // Executed only by the engine's own zoom (wheel, pinch, time-axis drag) — never by a
    // setBarSpace — so every call is the user choosing a bar width.
    const onZoom = () => onBarSpaceChangeRef.current?.(instance.getBarSpace())
    instance.subscribeAction(ActionType.OnZoom, onZoom)

    const ro = new ResizeObserver(() => chartRef.current?.resize())
    ro.observe(el)

    setChart(instance)

    // Captured up front: the lint rule can't know these refs are plain stores
    // that live exactly as long as this effect's chart instance.
    const markerIds = markerIdsRef.current
    const connectorIds = connectorIdsRef.current
    const orderLineIds = orderLineIdsRef.current
    return () => {
      ro.disconnect()
      instance.unsubscribeAction(ActionType.OnVisibleRangeChange, onRange)
      instance.unsubscribeAction(ActionType.OnZoom, onZoom)
      setChart(null)
      dispose(el)
      chartRef.current = null
      markerIds.clear()
      connectorIds.clear()
      orderLineIds.clear()
      orderLineSpecsRef.current = new Map()
      hoveredOrderKeyRef.current = null
      snapshotRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- Raw engine hand-off -------------------------------------------------
  // Driven off the `chart` STATE rather than called inside the init effect, so a consumer
  // that paints its own overlays gets a reactive signal and a matching null on dispose.
  const onChartReadyRef = useRef(onChartReady)
  onChartReadyRef.current = onChartReady
  useEffect(() => {
    onChartReadyRef.current?.(chart)
    return () => onChartReadyRef.current?.(null)
  }, [chart])

  // ---- Theme ---------------------------------------------------------------
  useEffect(() => {
    const c = chartRef.current
    if (!c) return
    c.setStyles(buildStyles(isDark, {}, CandleType.CandleSolid))
    if (showVolume) {
      c.overrideIndicator({ name: VOLUME_INDICATOR, extendData: volColors(isDark) }, 'candle_pane')
    }
  }, [isDark, chart, showVolume])

  // ---- Price precision from the symbol's point size ------------------------
  // The old chart hardcoded 5 decimals / minMove 0.00001, which is wrong for
  // metals, indices and crypto. Also drives klinecharts' y-axis autoscale
  // padding, so a coarse value squashes quiet windows into a thin strip.
  useEffect(() => {
    let cancelled = false
    setPricePrecision(null)
    fetchSymbolMeta(symbol)
      .then((meta) => {
        if (cancelled) return
        setPricePrecision(precisionFromPointSize(meta?.point_size))
      })
      .catch(() => {
        /* fall back to DEFAULT_PRECISION */
      })
    return () => {
      cancelled = true
    }
  }, [symbol])

  useEffect(() => {
    chartRef.current?.setPriceVolumePrecision(effectivePrecision, 0)
  }, [effectivePrecision, chart])

  // ---- Data ----------------------------------------------------------------
  // Strict suffix-append -> updateData per bar, which is the replay steady
  // state; anything else -> applyNewData. Re-uploading the whole array on every
  // replay tick was measurably too slow, hence the diff.
  //
  // `force` skips the fast path. Clearing a forming bar needs it: the engine has
  // no way to shrink its data list, so dropping that last element means
  // re-applying the closed array wholesale.
  const applyClosedData = useCallback(
    (force = false) => {
      const c = chartRef.current
      if (!c) return

      // Every branch below either overwrites or discards the forming bar.
      formingBarRef.current = null

      if (displayCandles.length === 0) {
        c.applyNewData([], false)
        snapshotRef.current = null
        markerIdsRef.current.clear()
        connectorIdsRef.current.clear()
        orderLineIdsRef.current.clear()
        orderLineSpecsRef.current = new Map()
        hoveredOrderKeyRef.current = null
        setDataNonce((n) => n + 1)
        return
      }

      const snapshot = snapshotRef.current
      const firstTime = displayCandles[0].time
      const lastTime = displayCandles[displayCandles.length - 1].time

      const canStrictAppend =
        !force &&
        snapshot !== null &&
        snapshot.firstTime === firstTime &&
        displayCandles.length > snapshot.count &&
        displayCandles[snapshot.count].time > snapshot.lastTime

      if (canStrictAppend) {
        // When a forming bar is painted, the first of these carries its exact
        // timestamp and so overwrites it in place with the real closed bar —
        // which is why the reveal never visibly jumps.
        for (let i = snapshot!.count; i < displayCandles.length; i++) {
          c.updateData(toKline(displayCandles[i]))
        }
      } else {
        // Unlike lightweight-charts' setData, applyNewData resets the scroll
        // offset to the live edge (resetOffsetRightDistance). Hold the current
        // index window instead; the parent then shifts/clamps it as it always did.
        const prev = closedIndexRange(c, displayCandles.length)
        c.applyNewData(displayCandles.map(toKline), false)
        if (prev) {
          const maxIdx = displayCandles.length - 1
          const span = Math.min(prev.to - prev.from, maxIdx)
          const to = Math.min(prev.to, maxIdx)
          setIndexRange(
            c,
            { from: Math.max(0, to - span), to },
            displayCandles.length,
            rightPadBarsRef.current,
            { keepZoom: fixedBarSpaceRef.current != null },
          )
        }
      }

      snapshotRef.current = { firstTime, lastTime, count: displayCandles.length }
      setDataNonce((n) => n + 1)
    },
    [displayCandles],
  )

  const applyClosedDataRef = useRef(applyClosedData)
  applyClosedDataRef.current = applyClosedData

  // useLayoutEffect so this lands before the parent's viewport layout effect
  // (child effects run first) and before paint.
  useLayoutEffect(() => {
    applyClosedData()

    const c = chartRef.current
    if (!c || displayCandles.length === 0) return
    if (autoSetInitialVisibleRange && !initialRangeSetRef.current) {
      initialRangeSetRef.current = true
      const visibleCount = getCandleCountForTimeframe(timeframe, displayCandles.length)
      const to = displayCandles.length - 1
      setIndexRange(
        c,
        { from: Math.max(0, to - visibleCount + 1), to },
        displayCandles.length,
        rightPadBarsRef.current,
        { keepZoom: fixedBarSpaceRef.current != null },
      )
    }
  }, [applyClosedData, displayCandles, timeframe, autoSetInitialVisibleRange, chart])

  useImperativeHandle(
    ref,
    () => ({
      setVisibleLogicalRange(range: IndexRange) {
        const c = chartRef.current
        if (!c) return
        // Deliberately the CLOSED count, even while a forming bar is painted:
        // the half-bar right margin that produces is exactly the gap the forming
        // bar occupies. Passing the engine's count instead would hide it.
        const n = displayCandlesRef.current.length
        const keepZoom = fixedBarSpaceRef.current != null
        let next = range
        // At a fixed zoom a range only says WHERE to look. At the live edge that is its newest
        // bar, which is `to` as given. Anywhere else it is the range's centre: a trade row asks for
        // a window centred on the trade, sized to a screen this chart is not holding, so keeping
        // its `to` would slide the trade off to one side.
        if (keepZoom && range.to < n - 1) {
          const v = engineVisibleBarCount(c)
          if (v != null) {
            const to = Math.round((range.from + range.to) / 2 + v / 2)
            next = { from: to - v, to }
          }
        }
        setIndexRange(c, next, n, rightPadBarsRef.current, { keepZoom })
      },
      getVisibleLogicalRange() {
        const c = chartRef.current
        return c ? closedIndexRange(c, displayCandlesRef.current.length) : null
      },
      getCandleCount() {
        return displayCandlesRef.current.length
      },
      getVisibleBarCount() {
        const c = chartRef.current
        return c ? engineVisibleBarCount(c) : null
      },
      setFormingBar(candle: FormingBarState | null, revealTs?: number) {
        const c = chartRef.current
        if (!c) return

        if (candle === null) {
          if (formingBarRef.current === null) return
          // applyClosedData bumps dataNonce, which re-reconciles overlays back to
          // bar granularity — so intra-bar arrows are dropped with the bar.
          applyClosedDataRef.current(true)
          return
        }

        const closedCount = displayCandlesRef.current.length
        if (closedCount === 0) return

        // Decide append-vs-overwrite against the engine's live last bar rather
        // than our own bookkeeping, so an applyNewData that wiped the forming bar
        // (timeframe change, window reload, prune) self-heals on the next frame.
        const list = c.getDataList()
        const engineLastMs = list.length > 0 ? list[list.length - 1].timestamp : null
        const candleMs = candle.time * 1000
        // Older than the engine's last bar is a silent no-op inside updateData.
        // Skip rather than pretend it painted; the next frame revalidates.
        if (engineLastMs !== null && candleMs < engineLastMs) return

        formingSuppressRef.current = true
        try {
          c.updateData(toKline(candle))

          // Keep the forming bar on screen. In the replay steady state the right
          // margin is already half a bar wide and this is a no-op. After a head
          // prune or a window reload that margin is gone and the appended bar
          // lands off-screen — so nudge one bar right, but only when the view is
          // pinned to the last closed bar. If the user has scrolled back into
          // history, leave their view alone.
          const vr = getIndexRange(c)
          if (vr && vr.to === closedCount - 1) {
            const span = vr.to - vr.from
            setIndexRange(
              c,
              { from: Math.max(0, closedCount - span), to: closedCount },
              closedCount + 1,
              rightPadBarsRef.current,
              { keepZoom: fixedBarSpaceRef.current != null },
            )
          }
        } finally {
          formingSuppressRef.current = false
        }

        formingBarRef.current = candle.time

        // After updateData, so the forming bar is already in the engine's data
        // list and an overlay anchored to its timestamp resolves to it rather
        // than to the last closed bar.
        if (revealTs !== undefined) reconcileOverlaysRef.current(revealTs, candle)

        for (const listener of repaintListenersRef.current) listener()
      },
    }),
    [],
  )

  // ---- Trade markers + entry→exit connectors -------------------------------
  // Both sets are diffed against their live overlay maps: klinecharts iterates
  // every overlay on each repaint, and a remove-all/create-all pass per replay
  // tick would crawl. In the steady state the delta is zero or one overlay.
  //
  // Connectors are derived purely from the 'closed' event, whose `time` IS the
  // exit time, so a connector appears whole once its exit is revealed.
  //
  // ONE implementation, two callers: the data effect below (whole revealed set)
  // and setFormingBar (the sub-bar cursor). Because it computes the full desired
  // key set and diffs create/remove, it is idempotent — calling it from two
  // places converges with no orphaned or duplicated overlays, and the two id maps
  // stay the single source of truth. A second, independent reveal path writing
  // the same maps is exactly how those bugs happen.
  /**
   * Push the current hover state onto the live order-line overlays.
   *
   * Deliberately `overrideOverlay` rather than remove-and-recreate. An overlay that is
   * destroyed can never deliver its own `mouseleave`, so rebuilding the set in order to
   * light one of them loses the un-hover for the very overlay the pointer is sitting on —
   * and the lit set stays lit with no way back to dim. Keeping the identity stable and
   * changing only `extendData` sidesteps that entirely.
   *
   * `overrideOverlay` REPLACES extendData rather than merging it, so the whole object is
   * rebuilt from the spec every time. That is also why the specs are kept: the id alone
   * cannot tell us what the labels were.
   */
  const applyOrderLineDim = useCallback(() => {
    const c = chartRef.current
    if (!c) return
    const lit = hoveredOrderKeyRef.current
    for (const [key, id] of orderLineIdsRef.current) {
      const spec = orderLineSpecsRef.current.get(key)
      if (!spec) continue
      c.overrideOverlay({
        id,
        extendData: orderLineExtendData(spec, precisionRef.current, lit !== null && lit !== key),
      })
    }
  }, [])
  const applyOrderLineDimRef = useRef(applyOrderLineDim)
  applyOrderLineDimRef.current = applyOrderLineDim

  const reconcileOverlays = useCallback(
    (revealTs: number, forming: FormingBarState | null) => {
      const c = chartRef.current
      if (!c) return

      const displayCandles = displayCandlesRef.current
      const timeframe = timeframeRef.current
      const allOrders = ordersRef.current
      // Infinity = bar granularity; the upstream reveal filter has already run.
      const orders =
        revealTs === Infinity ? allOrders : allOrders.filter((o) => o.time <= revealTs)

      // Built from the already-reveal-filtered orders, so "closed" here means "closed as
      // far as the playhead knows" — which is what both the outcome colouring and the
      // still-open test need, and why neither has to look at the cursor itself.
      const closes = liveOrderLinesRef.current ? closesByEntryTime(orders) : null
      const orderLines = closes
        ? buildOrderLines(displayCandles, orders, closes, pipSizeRef.current, MAX_OVERLAYS)
        : []

      const markers = buildMarkers(displayCandles, orders, timeframe, forming, closes)
      const connectors = buildConnectors(
        displayCandles,
        orders,
        timeframe,
        MAX_OVERLAYS - markers.length - orderLines.length,
        forming,
      )

      const reconcile = (
        live: Map<string, string>,
        keys: Set<string>,
        create: (key: string) => void,
      ) => {
        for (const [key, id] of live) {
          if (!keys.has(key)) {
            c.removeOverlay(id)
            live.delete(key)
          }
        }
        for (const key of keys) {
          if (!live.has(key)) create(key)
        }
      }

      const markerByKey = new Map(markers.map((s) => [s.key, s]))
      reconcile(markerIdsRef.current, new Set(markerByKey.keys()), (key) => {
        const spec = markerByKey.get(key)!
        const id = c.createOverlay({
          name: TRADE_MARKER,
          points: [{ timestamp: spec.barTime * 1000, value: spec.value }],
          extendData: { dir: spec.dir, color: spec.color, text: spec.text },
        })
        if (typeof id === 'string') markerIdsRef.current.set(key, id)
      })

      const connectorByKey = new Map(connectors.map((s) => [s.key, s]))
      reconcile(connectorIdsRef.current, new Set(connectorByKey.keys()), (key) => {
        const spec = connectorByKey.get(key)!
        const id = c.createOverlay({
          name: TRADE_CONNECTOR,
          points: spec.points,
          extendData: { color: spec.color },
        })
        if (typeof id === 'string') connectorIdsRef.current.set(key, id)
      })

      // The live sets last. A trade that just closed loses its lines in the same pass that
      // recolours its arrows, so the two never disagree for a frame.
      const orderLineByKey = new Map(orderLines.map((s) => [s.key, s]))
      // Replaced wholesale rather than mutated, so a closed trade's spec is pruned for free.
      orderLineSpecsRef.current = orderLineByKey
      const liveKeys = new Set(orderLineByKey.keys())
      // A hovered set that has since closed leaves nothing to un-dim, so drop the lit key
      // with it rather than leaving every other set faded with no way back.
      if (hoveredOrderKeyRef.current && !liveKeys.has(hoveredOrderKeyRef.current)) {
        hoveredOrderKeyRef.current = null
      }
      reconcile(orderLineIdsRef.current, liveKeys, (key) => {
        const spec = orderLineByKey.get(key)!
        const id = c.createOverlay({
          name: ORDER_LINES,
          points: spec.points,
          // The dim flag is seeded from the live hover state: a trade opening while another
          // one is lit has to arrive already faded, or it would be the only set at full
          // strength.
          extendData: orderLineExtendData(
            spec,
            precisionRef.current,
            hoveredOrderKeyRef.current !== null && hoveredOrderKeyRef.current !== key,
          ),
          onMouseEnter: () => {
            hoveredOrderKeyRef.current = key
            applyOrderLineDimRef.current()
            return false
          },
          onMouseLeave: () => {
            if (hoveredOrderKeyRef.current !== key) return false
            hoveredOrderKeyRef.current = null
            applyOrderLineDimRef.current()
            return false
          },
        })
        if (typeof id === 'string') orderLineIdsRef.current.set(key, id)
      })

      const total = markers.length + connectors.length + orderLines.length
      if (import.meta.env.DEV && total >= MAX_OVERLAYS) {
        console.warn(
          `[BacktestChart] overlays capped at ${MAX_OVERLAYS} ` +
            `(${markers.length} markers + ${connectors.length} connectors + ` +
            `${orderLines.length} order lines); older trades in this window are not drawn`,
        )
      }
    },
    [],
  )

  const reconcileOverlaysRef = useRef(reconcileOverlays)
  reconcileOverlaysRef.current = reconcileOverlays

  useEffect(() => {
    reconcileOverlays(Infinity, null)
    // displayCandles/orders/timeframe/liveOrderLines/pipSize are read through refs, but they
    // are what makes the result change, so they stay in the dep list.
  }, [reconcileOverlays, displayCandles, orders, timeframe, liveOrderLines, pipSize, dataNonce, chart])

  // ---- Interactions --------------------------------------------------------
  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    dragStartXRef.current = event.clientX
    dragNavigatedRef.current = false
  }, [])

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (dragStartXRef.current === null || dragNavigatedRef.current) return
      if (Math.abs(event.clientX - dragStartXRef.current) < 8) return
      dragNavigatedRef.current = true
      hideTooltip()
      onUserNavigateRef.current?.()
    },
    [hideTooltip],
  )

  const clearPointerDrag = useCallback(() => {
    dragStartXRef.current = null
    dragNavigatedRef.current = false
  }, [])

  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    hideTooltip()
    // Where the finger landed, so touchend can tell a tap from a pan. Multi-touch is a pinch,
    // never a tap, so it records nothing and the tap check below fails closed.
    const t = event.touches.length === 1 ? event.touches[0] : null
    touchStartRef.current = t ? { x: t.clientX, y: t.clientY } : null
    onUserNavigateRef.current?.()
  }, [hideTooltip])

  // Every wheel gesture counts as navigation now that the engine zooms on
  // vertical wheel (lightweight-charts had handleScale.mouseWheel disabled, so
  // only horizontal/shift wheel used to mean anything).
  const handleWheel = useCallback(() => {
    hideTooltip()
    onUserNavigateRef.current?.()
  }, [hideTooltip])

  /** Open the order card for the bar under a point, in container pixels. */
  const openOrderCardAt = useCallback((x: number, y: number) => {
    const c = chartRef.current
    if (!c) return
    const res = c.convertFromPixel([{ x, y }], { paneId: 'candle_pane', absolute: true })
    const point = Array.isArray(res) ? res[0] : res
    const dataIndex = point?.dataIndex

    const bars = displayCandlesRef.current
    if (dataIndex == null || dataIndex < 0 || dataIndex >= bars.length) {
      setTooltip(null)
      return
    }

    // Orders on the clicked bar: [barTime, barTime + timeframe).
    const barTime = bars[dataIndex].time
    const barEnd = barTime + getTimeframeSeconds(timeframeRef.current)
    const matched = ordersRef.current.filter((o) => o.time >= barTime && o.time < barEnd)
    setTooltip(matched.length > 0 ? { orders: matched, point: { x, y } } : null)
  }, [])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const el = containerRef.current
      if (!el || dragNavigatedRef.current) return
      const rect = el.getBoundingClientRect()
      openOrderCardAt(event.clientX - rect.left, event.clientY - rect.top)
    },
    [openOrderCardAt],
  )

  /**
   * A TAP HAS TO OPEN THE CARD ITSELF, because no click ever arrives on a touch device.
   *
   * klinecharts calls preventDefault() on touchend whenever it has a tap handler of its own —
   * which is always (SyntheticEvent._touchEndHandler: "do not fire mouse events if tap handler
   * was executed") — and that suppresses the compatibility click the browser would otherwise
   * synthesize. So handleClick above is a mouse-only route, and the phone was left with markers
   * it could see and not open.
   */
  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const start = touchStartRef.current
      touchStartRef.current = null
      // One finger, all of it lifted, and it barely moved. Anything else is a pan or a pinch,
      // and the chart has already treated it as navigation.
      if (!start || event.touches.length > 0 || event.changedTouches.length !== 1) return
      const t = event.changedTouches[0]
      // Same 8px the pointer drag check uses, as a manhattan distance — klinecharts cancels its
      // own tap on a comparable threshold, so the two agree about what a tap is.
      if (Math.abs(t.clientX - start.x) + Math.abs(t.clientY - start.y) > 8) return
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      openOrderCardAt(t.clientX - rect.left, t.clientY - rect.top)
    },
    [openOrderCardAt],
  )

  return (
    <div
      data-testid="backtest-chart"
      className="relative h-full w-full"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={clearPointerDrag}
      onPointerCancel={clearPointerDrag}
      onPointerLeave={clearPointerDrag}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onClick={handleClick}
    >
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{ backgroundColor: defaultBackground(isDark) }}
      />
      {chart && (
        <BacktestChartLegend
          chart={chart}
          precision={effectivePrecision}
          dataNonce={dataNonce}
          subscribeRepaint={subscribeRepaint}
        />
      )}
      {tooltip && containerRef.current && (
        <BacktestOrderTooltip
          orders={tooltip.orders}
          point={tooltip.point}
          containerWidth={containerRef.current.clientWidth}
          precision={effectivePrecision}
          tickets={orderTickets}
        />
      )}
    </div>
  )
})

export default BacktestChart
